import { useState, useContext } from "react";
import { useNavigate } from "react-router";
import axios from 'axios';

import { AppContext } from '../../context.js';

import { MoreVertical, Comment, ChevronDown, Refresh, ChevronUp, ArrowRight, CalendarEdit, Headphones } from "../../files/icons/index.js";

import { Status, Button } from './component.js';
import Editor from "./editor.js";
import Drop from "../components/drop.js";
import Chat from "../components/chat.js";
import { DatePicker } from "../components/datePicker.js";
import RecurrencePicker from "../components/recurrencePicker.js";
import PhysicianPicker from "../components/physicianPicker.js";
import { FullName } from '../../utils/string.js';

import { DateToFormat, DatetimeToDate, InRangeUTC } from "../../utils/datetime.js";

function AppointmentCard(props) {
  const context = useContext(AppContext);

  const { appointment: initialAppointment, users, settings } = props;

  const [appointment, setAppointment] = useState(initialAppointment);

  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const [closeAppointmentStatusDrop, setCloseAppointmentStatusDrop] = useState(0);

  const [changeAppointmentStatusModalVisible, setChangeAppointmentStatusModalVisible] = useState(false);
  const [changeAppointmentStatusModalData, setChangeAppointmentStatusModalData] = useState(null);

  // Update appointment created at
  const [updateAppointmentCreatedAtData, setUpdateAppointmentCreatedAtData] = useState({
    visible: false,
    id: null,
    date: null,
    reason: null,
  });

  // Update appointment closed at
  const [updateAppointmentClosedAtData, setUpdateAppointmentClosedAtData] = useState({
    visible: false,
    id: null,
    date: null,
    reason: null,
  });

  const [expandedAppointmentsChats, setExpandedAppointmentsChats] = useState(() => initialAppointment.hasNotes ? [initialAppointment.id] : []);

  // Functions
  function changeAppointmentStatus(appointmentID, status) {
    if (status.reasons.length > 0) {
      setChangeAppointmentStatusModalData({status, appointmentID});
      setChangeAppointmentStatusModalVisible(true);
      return;
    }

    updateAppointmentStatus(appointmentID, status.id, null);
  }

  function changeAppointmentStatusConfirm(appointmentID, statusID, reasonID) {
    setChangeAppointmentStatusModalVisible(false);
    setChangeAppointmentStatusModalData(null);
    updateAppointmentStatus(appointmentID, statusID, reasonID);
  }

  function updateAppointmentStatus(appointmentID, statusID, reasonID) {
    let u = context.addSpiner();
    axios.post('/api/admin/appointments/updateStatus', {
      id: appointmentID,
      statusID: statusID,
      reasonID: reasonID,
    }).then((response) => {
      setAppointment({
        ...appointment,
        statusType: response.data.statusType,
        statusID: response.data.statusID,
        statusName: response.data.statusName,
        statusReasonID: response.data.statusReasonID,
        statusReasonName: response.data.statusReasonName,

        closedAt: response.data.closedAt,
        closedBy: response.data.closedBy,
        closedByName: response.data.closedByName,
      });
      setCloseAppointmentStatusDrop(prev => prev + 1);
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function changeAppointmentTitle(id, text) {
    setAppointment(prevAppointment => ({ ...prevAppointment, title: text }));
  }

  function keyUpAppointmentTitle(e) {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  }

  function updateAppointmentTitle(id) {
    let u = context.addSpiner();
    axios.post('/api/admin/appointments/updateTitle', {
      id: appointment.id,
      title: appointment.title
    }).then((response) => {

    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function updateAppointmentDescription(text, id) {
    if (text === "" || text === "<p><br></p>") {
      text = null;
    }

    let u = context.addSpiner();
    axios.post('/api/admin/appointments/updateDescription', {
      id: appointment.id,
      description: text
    }).then((response) => {
      setAppointment(prevAppointment => ({ ...prevAppointment, description: text }));
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function updateAppointmentNote(text, id) {
    if (text === "" || text === "<p><br></p>") {
      text = null;
    }

    let u = context.addSpiner();
    axios.post('/api/admin/appointments/updateNote', {
      id: appointment.id,
      note: text
    }).then((response) => {
      setAppointment(prevAppointment => ({ ...prevAppointment, note: text }));
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function updateAppointmentUser(user, id) {
    let u = context.addSpiner();
    axios.post('/api/admin/appointments/updateUser', {
      id: id,
      userID: user.id
    }).then((response) => {
      setAppointment(prevAppointment => ({ ...prevAppointment, userID: user.id, userName: FullName(user.firstName, user.middleName, user.lastName) }));
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function updateAppointmentDeadline(date, id) {
    let u = context.addSpiner();
    axios.post('/api/admin/appointments/updateDeadline', {
      id: id,
      deadline: date
    }).then((response) => {
      setAppointment(prevAppointment => ({ ...prevAppointment, deadlineAt: date }));
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function expandAppointmentChat(id) {
    // Open/close chat
    if (expandedAppointmentsChats.includes(id)) {
      setExpandedAppointmentsChats(prev => prev.filter((item) => item !== id));
    } else {
      setExpandedAppointmentsChats(prev => [...prev, id]);
    }
  }

  function updateAppointmentRecurrence(appointmentID, intervalType, intervalDuration, fromType) {
    let u = context.addSpiner();
    axios.post('/api/admin/appointments/updateRecurrence', {
      id: appointmentID,
      intervalType: intervalType,
      intervalDuration: intervalDuration,
      fromType: fromType,
    }).then((response) => {
      setAppointment(prevAppointment => ({ ...prevAppointment, recurreAt: response.data.recurreAt }));
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function createAppointmentZoom(appointmentID) {
    let u = context.addSpiner();
    axios.post('/api/admin/appointments/zoom/create', {
      id: appointmentID,
    }).then((response) => {
      setAppointment(prevAppointment => ({
        ...prevAppointment,
        zoomMeetingID: response.data.zoomMeetingID,
        zoomStartURL: response.data.zoomStartURL,
        zoomJoinURL: response.data.zoomJoinURL,
      }));
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function updateAppointmentCreatedAt() {
    let u = context.addSpiner();
    axios.post('/api/admin/appointments/updateCreatedAt', {
      id: updateAppointmentCreatedAtData.id,
      createdAt: updateAppointmentCreatedAtData.date,
      reason: updateAppointmentCreatedAtData.reason,
    }).then((response) => {
      setAppointment(prevAppointment => ({ ...prevAppointment, createdAt: updateAppointmentCreatedAtData.date }));
      setUpdateAppointmentCreatedAtData({ visible: false, id: null, date: null, reason: null });
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function updateAppointmentClosedAt() {
    let u = context.addSpiner();
    axios.post('/api/admin/appointments/updateClosedAt', {
      id: updateAppointmentClosedAtData.id,
      closedAt: updateAppointmentClosedAtData.date,
      reason: updateAppointmentClosedAtData.reason,
    }).then((response) => {
      setAppointment(prevAppointment => ({ ...prevAppointment, closedAt: updateAppointmentClosedAtData.date }));
      setUpdateAppointmentClosedAtData({ visible: false, id: null, date: null, reason: null });
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function renderAppointmentStatus(templateID, statusID) {
    const status = settings.appointmentTemplates.find((t) => t.id === templateID).statuses.find((s) => s.id === statusID);

    if (!status) {
      return (
        <></>
      );
    }

    return (
      <Status color={status.color} muted={true} size={'small'} subtext={status.title} />
    );
  }

  function renderAppointmentNextStatuses(appointmentID, templateID, statusID) {
    const currentStatus = settings.appointmentTemplates.find((t) => t.id === templateID).statuses.find((s) => s.id === statusID);

    if (!currentStatus) {
      return (
        <></>
      );
    }

    if (!currentStatus.next) {
      return (
        <div className="typo-medium-150">
          Appointment has no next statuses
        </div>
      );
    }

    return settings.appointmentTemplates.find((t) => t.id === templateID).statuses.filter((status) => currentStatus.next.includes(status.id)).map((status) => {
      return (
        <div className="side-item small typo-medium-150" onClick={() => changeAppointmentStatus(appointmentID, status)}>
          To <ArrowRight height={14} width={16} color={'grey'} /> {renderAppointmentStatus(templateID, status.id)}
        </div>
      );
    })
  }

  function renderAppointment() {

    var datePickerColor = "grey";
    if (appointment.deadlineAt && appointment.statusType !== "COMPLETED" && appointment.statusType !== "CANCELED") {
      const today = new Date();
      const deadlineDate = new Date(appointment.deadlineAt);

      // Helper functions for today's range
      const firstMomentOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
      const lastMomentOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);

      // Helper functions for tomorrow's range
      const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
      const firstMomentOfTomorrow = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate(), 0, 0, 0, 0);
      const lastMomentOfTomorrow = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate(), 23, 59, 59, 999);

      // Check if deadline is tomorrow (today is previous day of deadline)
      if (InRangeUTC(deadlineDate, firstMomentOfTomorrow, lastMomentOfTomorrow)) {
        datePickerColor = "yellow";
      }
      // Check if deadline is today or overdue
      else if (InRangeUTC(deadlineDate, firstMomentOfToday, lastMomentOfToday) || new Date().getTime() > deadlineDate.getTime()) {
        datePickerColor = "red";
      }
    }

    return (
      <div key={"appointment_" + appointment.id} id={"appointment_" + appointment.id} className="card mb-2">
        <div className="card-header sm">
          <div className="d-flex justify-content-between align-items-center">
            <input type="text" className="input borderless typo-strong-200 w-100" placeholder="Title..." value={appointment.title} onChange={(e) => changeAppointmentTitle(appointment.id, e.target.value)} onBlur={() => updateAppointmentTitle(appointment.id)} onKeyUp={(e) => keyUpAppointmentTitle(e)} />
            <div className="d-flex justify-content-end align-items-center w-25">
              {renderAppointmentStatus(appointment.templateID, appointment.statusID)}
              <Drop close={closeAppointmentStatusDrop} side={"right"} className="ms-1">
                <div className="item rect">
                  <MoreVertical height={14} width={16} color={'grey'} />
                </div>
                <div className="card" style={{width: 200}}>
                  <div className="card-header sm">
                    <div className="typo-normal-150 text-muted">Change status:</div>
                    {renderAppointmentNextStatuses(appointment.id, appointment.templateID, appointment.statusID)}
                    <div className="typo-normal-150 text-muted">Change dates:</div>
                    <div className="side-item small typo-medium-150" onClick={() => setUpdateAppointmentCreatedAtData({ visible: true, id: appointment.id, date: appointment.createdAt, reason: null })}>
                      Change created at date
                    </div>
                    {appointment.closedAt && (
                      <div className="side-item small typo-medium-150" onClick={() => setUpdateAppointmentClosedAtData({ visible: true, id: appointment.id, date: appointment.closedAt, reason: null })}>
                        Change closed at date
                      </div>
                    )}
                    {!appointment.zoomMeetingID && (
                      <>
                        <div className="typo-normal-150 text-muted">Other actions:</div>
                        <div className="side-item small typo-medium-150" onClick={() => createAppointmentZoom(appointment.id)}>
                          Create Zoom Meeting
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </Drop>
            </div>
          </div>

          <Editor
            placeholder="Appointment description..."
            value={appointment.description}
            onChangeID={appointment.id}
            onBlur={updateAppointmentDescription}
            toolbar="limited"
            className="text-editor borderless"
          />

          {appointment.zoomMeetingID && (
            <div className="mt-2">
              <div className="item grey me-2">
                <Headphones height={16} width={16} color={'grey'} />
                <a href={appointment.zoomJoinURL} target="_blank" rel="noreferrer" className="typo-normal-150">Join Zoom Meeting</a>
              </div>
            </div>
          )}

          <div className="d-flex justify-content-start align-items-center mt-2">
            <div className="d-flex justify-content-start align-items-center me-3">
              <span className="typo-normal-150">created at {DateToFormat(DatetimeToDate(appointment.createdAt), context.dateFormat)} {DateToFormat(DatetimeToDate(appointment.createdAt), context.timeFormat === "us" ? "AMPM" : "HH:mm")}</span>
            </div>

            {appointment.closedAt && (
              <div className="d-flex justify-content-start align-items-center me-3">
                <span className="typo-normal-150">closed at {DateToFormat(DatetimeToDate(appointment.closedAt), context.dateFormat)} {DateToFormat(DatetimeToDate(appointment.closedAt), context.timeFormat === "us" ? "AMPM" : "HH:mm")}</span>
              </div>
            )}
          </div>

          <div className="d-flex justify-content-between align-items-center mt-2">
            <div className="d-flex justify-content-start align-items-center w-100">
              <PhysicianPicker
                physicianList={users}
                physicianID={appointment.userID}
                onChange={updateAppointmentUser}
                onChangeID={appointment.id}
                rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                className="me-2"
              />

              <DatePicker
                date={appointment.deadlineAt}
                onChange={updateAppointmentDeadline}
                onChangeID={appointment.id}
                leftIcon={<CalendarEdit height={16} width={16} color={'grey'} />}
                rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                className={"me-2 " + datePickerColor}
              />

              <RecurrencePicker
                recurreAt={appointment.recurreAt}
                onChange={updateAppointmentRecurrence}
                onChangeID={appointment.id}
                leftIcon={<Refresh height={16} width={16} color={'grey'} />}
                rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                className="me-2"
              />
            </div>
            <div className="d-flex justify-content-end align-items-bottom">
              <div onClick={() => expandAppointmentChat(appointment.id)}>
                {expandedAppointmentsChats.includes(appointment.id) ? (
                  <div className="item grey">
                    <Comment height={16} width={16} color={'grey'} />
                    <span className="typo-normal-150">Notes</span>
                    <ChevronUp height={14} width={16} color={'grey'} />
                  </div>
                ) : (
                  <div className="item grey">
                    <Comment height={16} width={16} color={'grey'} />
                    <span className="typo-normal-150">Notes</span>
                    <ChevronDown height={14} width={16} color={'grey'} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {expandedAppointmentsChats.includes(appointment.id) && (
          <div className="card-content sm">
            <Chat
              appointmentID={appointment.id}
            />
          </div>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <></>
    );
  }

  return (
    <>
      {updateAppointmentCreatedAtData.visible && (
        <div className="fixed-bottom modal-bg" onClick={(e) => setUpdateAppointmentCreatedAtData({ visible: false })}>
          <div className="container-fluid" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-4 col-lg-offset-4">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header">
                    <span className="typo-header-3xLarge">Update Appointment Creation Date</span><br/>
                    <span className="typo-normal-175">Select the correct date and reason of creation and click update button</span>
                  </div>
                  <div className="card-content">
                    <div className="d-flex justify-content-start align-items-center">
                      <DatePicker
                        date={updateAppointmentCreatedAtData.date}
                        onChange={(date) => setUpdateAppointmentCreatedAtData({...updateAppointmentCreatedAtData, date: date})}
                        onChangeID={null}
                        leftIcon={<CalendarEdit height={16} width={16} color={'grey'} />}
                        rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                        className="me-2"
                      />

                      <input type="text" className="input borderless typo-normal-150 w-100 ms-2" placeholder="Reason..." value={updateAppointmentCreatedAtData.reason} onChange={(e) => setUpdateAppointmentCreatedAtData({...updateAppointmentCreatedAtData, reason: e.target.value})} />
                    </div>
                  </div>
                  <div className="card-content text-end">
                    <Button onClick={updateAppointmentCreatedAt} color="blue" text="Update" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {updateAppointmentClosedAtData.visible && (
        <div className="fixed-bottom modal-bg" onClick={(e) => setUpdateAppointmentClosedAtData({ visible: false })}>
          <div className="container-fluid" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-4 col-lg-offset-4">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header">
                    <span className="typo-header-3xLarge">Update Appointment Completion Date</span><br/>
                    <span className="typo-normal-175">Select the correct date and reason of completion and click update button</span>
                  </div>
                  <div className="card-content">
                    <div className="d-flex justify-content-start align-items-center">
                      <DatePicker
                        date={updateAppointmentClosedAtData.date}
                        onChange={(date) => setUpdateAppointmentClosedAtData({...updateAppointmentClosedAtData, date: date})}
                        onChangeID={null}
                        leftIcon={<CalendarEdit height={16} width={16} color={'grey'} />}
                        rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                        className="me-2"
                      />

                      <input type="text" className="input borderless typo-normal-150 w-100 ms-2" placeholder="Reason..." value={updateAppointmentClosedAtData.reason} onChange={(e) => setUpdateAppointmentClosedAtData({...updateAppointmentClosedAtData, reason: e.target.value})} />
                    </div>
                  </div>
                  <div className="card-content text-end">
                    <Button onClick={updateAppointmentClosedAt} color="blue" text="Update" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {changeAppointmentStatusModalVisible && (
        <div className="fixed-bottom modal-bg" onClick={(e) => setChangeAppointmentStatusModalVisible(false)}>
          <div className="container-fluid" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-4 col-lg-offset-4">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header">
                    <span className="typo-header-3xLarge">Change Status</span><br/>
                    <span className="typo-normal-175">Select a reason for changing the status</span>
                  </div>
                  <div className="card-content">
                    {changeAppointmentStatusModalData && changeAppointmentStatusModalData.status.reasons.map((reason) => {
                      return (
                        <div key={reason.id} className="side-item small typo-medium-150" onClick={() => changeAppointmentStatusConfirm(changeAppointmentStatusModalData.appointmentID, changeAppointmentStatusModalData.status.id, reason.id)}>
                          {reason.title}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {renderAppointment()}
    </>
  );
}

export default AppointmentCard;